import store from '@/store'
import { UserId } from '@/core/Auth'
import { GradeKey } from '@/core/Grades'
import { PaymentId } from '@/externals/gyym/user/payments'
import { PointOnSale } from '@/core/Points'
import { SubscriptionPlan } from '@/core/PointSubscriptions'

interface GtmParams {
  [key: string]: string | number | string[] | number[] | object
}

export const pushDataLayer = (params: GtmParams): void => {
  const dataLayer = window.dataLayer || []
  dataLayer.push(params)
}

export const pushOpenPurchaseModalEvent = (params: {
  selectedPlanId?: string
  selectedBulkValue?: number
}) => {
  pushDataLayer({
    event: 'purchase_modal_open',
    purchase_modal_open: {
      user_id: store.getters['auth/user'].id,
      sex: store.getters['auth/user'].sex,
      age: store.getters['auth/user'].age,
      prefecture: store.getters['auth/user'].prefecture,
      postal_code: store.getters['auth/user'].postalCode,
      ...params,
    },
  })
}

export const pushPurchasePointEvent = (
  paymentId: PaymentId,
  point: PointOnSale
): void => {
  const purchasePoint = point.value + point.returnValue
  pushDataLayer({
    user_id: store.getters['auth/user'].id,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    purchase_point: purchasePoint,
    purchase_price: point.value,
    eventID: 'Purchase-' + paymentId,
    event: 'purchase',
  })
}

export const pushRegisterKarteEvent = (userId: UserId) => {
  pushDataLayer({
    user_id: userId,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'regist_karte',
  })
}

export const pushEditKarteEvent = (userId: UserId) => {
  pushDataLayer({
    user_id: userId,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'edit_karte',
  })
}

export const pushBeginSubscriptionEvent = (
  subscriptionId: string,
  plan: SubscriptionPlan,
  customerType: 'new' | 'repeat'
): void => {
  // 旧イベントも念のため残しておく
  pushDataLayer({
    user_id: store.getters['auth/user'].id,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    plan_id: plan.id,
    plan_name: plan.name,
    customer_type: customerType,
    event: 'purchase_subs',
  })

  // 新イベント
  pushDataLayer({
    subs_id: subscriptionId,
    user_id: store.getters['auth/user'].id,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    plan_id: plan.id,
    plan_name: plan.name,
    customer_type: customerType,
    event: 'begin_subscription',
  })
}

export const pushCancelSubscriptionEvent = (
  subscriptionId: string,
  plan: SubscriptionPlan
): void => {
  pushDataLayer({
    subs_id: subscriptionId,
    user_id: store.getters['auth/user'].id,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    plan_id: plan.id,
    plan_name: plan.name,
    event: 'cancel_subscription',
  })
}

type ReservationEventParams = {
  reservation_id: number
  point: number
  program_id: number
  facility_id: number
  timetable_id: number
  referral:
    | 'facility'
    | 'program'
    | 'online_facility'
    | 'online_program'
    | 'favorites'
}

export const pushReservationEvent = (params: ReservationEventParams): void => {
  pushDataLayer({
    eventID: 'Reserve-' + params.reservation_id,
    event: 'reserve',
    user_id: store.getters['auth/user'].id,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    ...params,
  })
}

export const pushSignupEvent = (): void => {
  pushDataLayer({
    user_id: store.getters['auth/user'].id,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'register',
    eventID: 'CompleteRegistration-' + store.getters['auth/user'].id,
  })
}

export const pushLoginEvent = (): void => {
  pushDataLayer({
    user_id: store.getters['auth/user'].id,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'login',
  })
}

export const pushModalRankUpEvent = (userId: UserId, gradeKey: GradeKey) => {
  pushDataLayer({
    user_id: userId,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'modal_rank_up',
    grade_key: gradeKey,
  })
}

export const pushModalRankDownEvent = (userId: UserId, gradeKey: GradeKey) => {
  pushDataLayer({
    user_id: userId,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'modal_rank_down',
    grade_key: gradeKey,
  })
}

export const pushModalCompleteQuestEvent = (
  userId: UserId,
  gradeKey: GradeKey
) => {
  pushDataLayer({
    user_id: userId,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'modal_complete_quest',
    grade_key: gradeKey,
  })
}

export const pushModalWelcomeBackEvent = (
  userId: UserId,
  gradeKey: GradeKey
) => {
  pushDataLayer({
    user_id: userId,
    sex: store.getters['auth/user'].sex,
    age: store.getters['auth/user'].age,
    prefecture: store.getters['auth/user'].prefecture,
    postal_code: store.getters['auth/user'].postalCode,
    event: 'modal_welcome_back',
    grade_key: gradeKey,
  })
}

export const pushSubscriptionCancelSurveyEvent = (values: {
  reasons: string[]
  intentToContinue: string
  otherReason?: string
}) => {
  pushDataLayer({
    event: 'subscription_cancel_survey',
    subscription_cancel_survey: {
      user_id: store.getters['auth/user'].id,
      sex: store.getters['auth/user'].sex,
      age: store.getters['auth/user'].age,
      prefecture: store.getters['auth/user'].prefecture,
      postal_code: store.getters['auth/user'].postalCode,
      reasons: values.reasons,
      intent_to_continue: values.intentToContinue,
      other_reason: values.otherReason ?? '',
    },
  })
}
